/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import styles from './note-menu.module.css' with { type: 'css' };
import html from './note-menu.module.html';
import { toNum, toInt, TunePadAudio, Synthesizer, Note, MusicKey } from '../../audio';
import { NoteRecorder } from '../mcc/mcc';
import { TunePadInstrument } from '../instrument';


/**
 * Select note durations from a popup menu
 * 
 * <tunepad-note-menu>
 */
export class NoteMenu extends HTMLElement {

    static readonly ELEMENT = "tunepad-note-menu";

    root : ShadowRoot;
    key : MusicKey = new MusicKey('C major');
    synth? : Synthesizer;
    mcc : NoteRecorder;

    // when the dialog window is closed, this promise is resolved
    promise? : (result : string) => void;

    // result that the promise function returns
    result : string = '';

    // which of several menus to show
    menu : string = 'notes'; // 'notes' | 'rests' | 'pitch' | 'chord' | 'drums'


    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.root.innerHTML = html;

        this.mcc = new NoteRecorder();
    }


    async connectedCallback() {

        // only show the correct menu
        this.root.querySelectorAll('.note-menu').forEach(m => { if (m.id != this.menu) m.remove(); });

        // initialize synth and audio services for interactive menus
        if ([ 'chord', 'pitch', 'drums' ].includes(this.menu)) {
            const audio = TunePadAudio.init();
            this.key = audio.key;
            this.synth = new Synthesizer();
            let url = new URL(`${audio.voices}/grand-piano/patch.json`, import.meta.url);
            if (this.menu === 'drums') {
                url = new URL(`${audio.voices}/rock-drums/patch.json`, import.meta.url);
            }
            await this.synth.loadPatch(url);
            if (this.menu === 'drums') {
                (this.root.querySelector('drums-instrument') as TunePadInstrument)?.setPatch(this.synth.patch, '808-drums');
            }
            this.setHTML('.key-name', this.key.name);
        }

        // set default note information
        if (this.menu === 'pitch') this.setNoteInfo(this.result);

        // do nothing if the user clicks on the background part of the dialog
        this.root.querySelector('.note-menu')?.addEventListener('pointerdown', e => { e.stopImmediatePropagation() } );

        // if they click on an option button or submit button close the dialog
        this.root.querySelectorAll('.option, .submit-button').forEach(opt => {
            opt.addEventListener('click', e => {
                this.hide();
                if (this.promise) this.promise(opt.getAttribute('data-action') || this.result);
            });
        });

        // listen for events from the piano instrument
        this.root.querySelector('.instrument')?.addEventListener('note-on', e => {
            const note = new Note((e as CustomEvent).detail.note);
            this.result = (this.menu === 'drums') ? `${note.note}` : this.key.getNoteVariableName(note);
            this.setNoteInfo(note);
            this.synth?.playNote(note);
            this.mcc.noteOn(note);
            if (this.menu === 'chord') {
                this.setChordInfo(this.mcc.currentNoteNumbers);
            } else if (this.menu === 'drums') {
                this.setHTML('.drum-name', (e as CustomEvent).detail.drum ?? '');
            }
        });
        this.root.querySelector('.instrument')?.addEventListener('note-off', e => {
            const note = new Note((e as CustomEvent).detail.note);
            this.synth?.releaseNote(note);
            this.mcc.noteOff(note);
        });

        // initialize the chord buttons for the diatonic chords of the current key
        for (let i=1; i<=7; i++) {
            const roman = this.key.getChordRoman(i);
            const name = this.key.getChordName(i);
            const button = document.createElement('button');
            button.classList.add('chord-button');
            button.setAttribute('data-chord', `${i}`);
            button.innerHTML = `<div class="emoji">${this.getChordEmoji(i)}</div><div class="name">${name}</div>`;
            this.root.querySelector('.chord-buttons')?.append(button);
            button.addEventListener('pointerdown', e => this.previewChord(button.getAttribute('data-chord') || '1'));
            button.addEventListener('pointerenter', e => {
                if ((e as PointerEvent).buttons > 0) {
                    this.previewChord(button.getAttribute('data-chord') || '1');
                }
            });
            button.addEventListener('pointerup', e => { this.synth?.releaseAll(); });
            button.addEventListener('pointerleave', e => { this.synth?.releaseAll(); })
        }
    }

    disconnectedCallback() {
        this.root.innerHTML = '';
        this.synth = undefined;
    }

    getChordEmoji(chordNum : number) {
        switch (chordNum) {
            case 1: return '😀';
            case 2: return '🙁';
            case 3: return '🤨';
            case 4: return '🤩';
            case 5: return '🥳';
            case 6: return '🥺';
            case 7: return '😵‍💫';
            default: return '😧';
        }
    }


    static show(menuName : string = 'notes', currentSelection : string = "1") : Promise<string> {
        const overlay = document.createElement('div');
        overlay.classList.add('context-menu-overlay', 'show');
        document.body.append(overlay);
        const menu = document.createElement('tunepad-note-menu') as NoteMenu;
        menu.result = currentSelection;
        menu.menu = menuName;
        overlay.append(menu);

        return new Promise((resolve) => {
            menu.promise = resolve;
            overlay.addEventListener('pointerdown', e => { 
                menu.hide();
                resolve(currentSelection);
            });
        });
    }

    hide() {
        const overlay = document.querySelector('.context-menu-overlay');
        if (overlay) {
            overlay.classList.remove('show');
            setTimeout(() => { 
                overlay.innerHTML = '';
                overlay.remove();
            }, 175);
        }
    }

    private setNoteInfo(n : Note | string) {
        const note = (typeof n === 'string')? new Note(toNum(n, 60)) : n;
        if (this.menu === 'drums') {
            this.setHTML('#select-note', `Select Note ${note.note}`);
        } else {
            this.setHTML('#select-note', `Select Note ${this.key.getNoteName(note, true)}${note.octave}`);
        }
        this.setHTML('.note-name .name', this.key.getNoteName(note, false));
        this.setHTML('.note-name .accidental', this.key.getAccidental(note));
        this.setHTML('.note-octave', `${note.octave}`);
        this.setHTML('.note-number', `${note.note}`);
        this.setBackground('.note-name', note.stepColor);
    }

    private previewChord(c : string) {
        const chord = toInt(c, 1);
        if (chord >= 1 && chord <= 7) {
            const notes = this.key.getDiatonicTriad(chord);
            notes.forEach(n => this.synth?.playNote(n));
            this.setChordInfo(notes);
        }
    }

    private setChordInfo(notes : number []) {
        const names = notes.map(n => this.key.getNoteName(new Note(n)));
        const vars = notes.map(n => this.key.getNoteVariableName(new Note(n)));
        this.setHTML('.note-numbers', `[ ${notes.join(', ')} ]`);
        this.setHTML('.note-names', `${names.join(', ')}`);
        this.result = `[${vars.join(', ')}]`;
    }

    private setHTML(selector : string, html : string) {
        const el = this.root.querySelector(selector);
        if (el) el.innerHTML = html;
    }

    private setBackground(selector : string, color : string) {
        const el = this.root.querySelector(selector);
        if (el) (el as HTMLElement).style.backgroundColor = color;
    }
}
